import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Kicker } from '@/components/Kicker';
import { Search, Users2 } from 'lucide-react';
import { OfferManagerDialog } from '@/components/OfferManagerDialog';

export default function AdminProducts() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [vis, setVis] = useState('all');
  const [offersFor, setOffersFor] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'products'], queryFn: async () => (await api.get('/admin/products')).data });
  const rows = useMemo(() => {
    let items = data?.items || [];
    if (vis !== 'all') items = items.filter((p) => p.visibility === vis);
    const ql = q.toLowerCase().trim();
    if (ql) items = items.filter((p) =>
      (p.name || '').toLowerCase().includes(ql) ||
      (p.default_code || '').toLowerCase().includes(ql) ||
      (p.category_path || '').toLowerCase().includes(ql)
    );
    return items;
  }, [data, q, vis]);

  return (
    <section>
      <Kicker>{t('admin.title')}</Kicker>
      <h1 className="mt-3 font-display text-3xl font-semibold text-[#14171C]">{t('admin.nav.products')}</h1>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4A4D52]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('catalog.search_placeholder')} className="h-11 w-full rounded-full border border-[#E4E9F0] bg-white pl-11 pr-4 text-sm outline-none focus:border-[#E2611C]" data-testid="admin-products-search" />
        </div>
        <div className="flex gap-2">
          {[['all', 'Alle'], ['public', t('admin.products.public')], ['customer_specific', t('admin.products.customer')]].map(([k, label]) => (
            <button key={k} onClick={() => setVis(k)} data-testid={`admin-products-filter-${k}`} className={`rounded-full border px-4 py-2 text-xs font-semibold ${vis === k ? 'border-[#E2611C] bg-[#FBEADD] text-[#E2611C]' : 'border-[#E4E9F0] bg-white text-[#14171C] hover:border-[#14171C]/25'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 pn-card overflow-hidden">
        <table className="w-full text-left text-sm" data-testid="admin-products-table">
          <thead className="bg-[#F6F8FB] text-xs uppercase tracking-wide text-[#4A4D52]">
            <tr>
              <th className="px-4 py-3">Odoo ID</th>
              <th className="px-4 py-3">Produkt</th>
              <th className="px-4 py-3">Ref</th>
              <th className="px-4 py-3">Kategorie</th>
              <th className="px-4 py-3">Bestand</th>
              <th className="px-4 py-3">Dokumente</th>
              <th className="px-4 py-3">{t('admin.products.visibility')}</th>
              <th className="px-4 py-3 text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="p-6 text-center text-[#4A4D52]">{t('common.loading')}</td></tr>}
            {!isLoading && rows.map((p) => (
              <tr key={p._odoo_id} className="border-t border-[#E4E9F0] hover:bg-[#F6F8FB]">
                <td className="px-4 py-3 font-mono text-xs text-[#4A4D52]">{p._odoo_id}</td>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.default_code || '—'}</td>
                <td className="px-4 py-3 text-[#4A4D52]">{p.category_path || p.category_name || '—'}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.qty_available ?? '—'}</td>
                <td className="px-4 py-3 font-mono text-xs">{p.documents_count}</td>
                <td className="px-4 py-3">
                  {p.visibility === 'public' ? (
                    <span className="inline-flex rounded-full bg-[#FBEADD] px-2.5 py-0.5 text-xs font-semibold text-[#E2611C]">{t('admin.products.public')}</span>
                  ) : (
                    <span className="inline-flex rounded-full bg-[#F6F8FB] px-2.5 py-0.5 text-xs font-semibold text-[#14171C]">{t('admin.products.customer')}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setOffersFor(p)} data-testid="admin-product-offers" className="inline-flex items-center gap-1 rounded-full bg-[#E2611C] px-3 py-1 text-xs font-semibold text-white hover:brightness-95">
                    <Users2 className="h-3.5 w-3.5" /> {t('offers.manage_for_product')}
                  </button>
                </td>
              </tr>
            ))}
            {!isLoading && rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#4A4D52]">{t('catalog.empty')}</td></tr>}
          </tbody>
        </table>
      </div>

      <OfferManagerDialog
        open={!!offersFor}
        onOpenChange={(v) => !v && setOffersFor(null)}
        mode="by-product"
        target={offersFor}
      />
    </section>
  );
}
